import React, { useMemo } from 'react';
import './styles.scss';
import Typography from 'Components/Atoms/Typography';
import { ReservationShift } from 'types/shifts';
import { blockToTime } from 'utils/helper';
import {
  shiftTableBlockTimeFields,
  TableBlockTimeFields,
} from 'utils/tableBlocks';

export type SegmentState = 'free' | 'locked' | 'willLock' | 'willUnlock';

export type ShiftSegment = {
  shift: ReservationShift;
  fields: TableBlockTimeFields;
  /** Row inside the track; shifts that run at the same time get their own. */
  lane: number;
};

const TICK_STEP = 8; // 2 hours
const LANE_HEIGHT = 44;
const LANE_GAP = 4;

/**
 * Lays the day's shifts out as lanes on one axis. Shared by the table and the
 * space block dialog so both read the same way.
 */
export const useShiftSegments = (
  /** Full shift list of the day — the window math needs every shift. */
  shifts: ReservationShift[],
  /** Subset the dialog may offer, e.g. only shifts reaching this table. */
  visible: ReservationShift[]
) => {
  const segments: ShiftSegment[] = useMemo(() => {
    const withWindow = visible
      .filter((s) => !!s.id)
      .map((s) => ({ shift: s, fields: shiftTableBlockTimeFields(shifts, s.id) }))
      .filter(
        (s): s is { shift: ReservationShift; fields: TableBlockTimeFields } =>
          !!s.fields
      )
      .sort((a, b) => a.fields.startTimeInBlocks - b.fields.startTimeInBlocks);

    // Restaurants run shifts in parallel (same window, different occasions), so
    // give every overlapping shift its own row instead of stacking them.
    const laneEnds: number[] = [];
    return withWindow.map((entry) => {
      let lane = laneEnds.findIndex(
        (end) => end <= entry.fields.startTimeInBlocks
      );
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = entry.fields.endTimeInBlocks;
      return { ...entry, lane };
    });
  }, [visible, shifts]);

  const range = useMemo(() => {
    if (!segments.length) return null;
    const start = segments[0].fields.startTimeInBlocks;
    const close = segments.reduce(
      (acc, s) => Math.max(acc, s.fields.endTimeInBlocks),
      start
    );
    return close > start ? { start, close } : null;
  }, [segments]);

  return { segments, range };
};

export type ShiftBlockTimelineProps = {
  segments: ShiftSegment[];
  range: { start: number; close: number } | null;
  stateOf: (shiftId: string) => SegmentState;
  onToggle: (shiftId: string) => void;
  /** Segments that cannot be toggled, e.g. closed by another rule. */
  isDisabled?: (shiftId: string) => boolean;
  /** Tooltip for a segment, e.g. why it cannot be toggled. */
  titleOf?: (shiftId: string) => string | undefined;
  /** False where changes are written immediately and nothing stays pending. */
  showPendingLegend?: boolean;
};

const ShiftBlockTimeline = ({
  segments,
  range,
  stateOf,
  onToggle,
  isDisabled,
  titleOf,
  showPendingLegend = true,
}: ShiftBlockTimelineProps) => {
  const laneCount = useMemo(
    () => segments.reduce((acc, s) => Math.max(acc, s.lane + 1), 1),
    [segments]
  );

  const positionOf = (
    fields: Pick<TableBlockTimeFields, 'startTimeInBlocks' | 'endTimeInBlocks'>,
    lane?: number
  ) => {
    if (!range) return { left: '0%', width: '0%' };
    const span = range.close - range.start;
    const horizontal = {
      left: `${((fields.startTimeInBlocks - range.start) / span) * 100}%`,
      width: `${((fields.endTimeInBlocks - fields.startTimeInBlocks) / span) * 100}%`,
    };

    if (lane === undefined) return horizontal;

    return {
      ...horizontal,
      top: lane * (LANE_HEIGHT + LANE_GAP),
      height: LANE_HEIGHT,
    };
  };

  const ticks = useMemo(() => {
    if (!range) return [];
    const out: number[] = [];
    for (let b = range.start; b <= range.close; b += TICK_STEP) out.push(b);
    return out;
  }, [range]);

  // Gaps are the times no shift covers at all, so they come from the merged
  // union of every shift window, not from neighbouring segments.
  const gaps = useMemo(() => {
    const merged: { start: number; close: number }[] = [];

    segments.forEach(({ fields }) => {
      const last = merged[merged.length - 1];
      if (last && fields.startTimeInBlocks <= last.close) {
        last.close = Math.max(last.close, fields.endTimeInBlocks);
      } else {
        merged.push({
          start: fields.startTimeInBlocks,
          close: fields.endTimeInBlocks,
        });
      }
    });

    return merged
      .slice(0, -1)
      .map((block, i) => ({ start: block.close, close: merged[i + 1].start }))
      .filter((gap) => gap.close > gap.start);
  }, [segments]);

  if (!range) return null;

  return (
    <div className="shiftBlockTimeline">
      <div
        className="tbm-track"
        style={{ height: laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP }}
      >
        {/* A span, not a disabled button: disabled elements disappear from
            the accessibility tree, so screen readers would never learn the
            gap exists. */}
        {gaps.map((gap) => (
          <span
            key={`gap-${gap.start}`}
            className="tbm-segment gap"
            style={positionOf({
              startTimeInBlocks: gap.start,
              endTimeInBlocks: gap.close,
            })}
            title={`Kein Service · ${blockToTime(gap.start)} bis ${blockToTime(
              gap.close
            )}`}
          >
            <span className="tbm-segTime">kein Service</span>
          </span>
        ))}
        {segments.map(({ shift, fields, lane }) => {
          const state = stateOf(shift.id);
          return (
            <button
              key={shift.id}
              type="button"
              className={`tbm-segment ${state}`}
              style={positionOf(fields, lane)}
              aria-pressed={state === 'locked' || state === 'willLock'}
              disabled={isDisabled?.(shift.id) ?? false}
              title={titleOf?.(shift.id)}
              onClick={() => onToggle(shift.id)}
            >
              <span className="tbm-segLabel">{shift.name ?? shift.id}</span>
              <span className="tbm-segTime">
                {blockToTime(fields.startTimeInBlocks)} bis{' '}
                {blockToTime(fields.endTimeInBlocks)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tbm-ticks">
        {ticks.map((tick) => (
          <span
            key={tick}
            className="tbm-tick"
            style={{
              left: `${((tick - range.start) / (range.close - range.start)) * 100}%`,
            }}
          >
            {blockToTime(tick)}
          </span>
        ))}
      </div>

      <div className="tbm-legend">
        <span className="tbm-legendItem">
          <span className="tbm-swatch" />
          <Typography variant="text-3" color="subdued" translation={null}>
            Frei
          </Typography>
        </span>
        <span className="tbm-legendItem">
          <span className="tbm-swatch locked" />
          <Typography variant="text-3" color="subdued" translation={null}>
            Gesperrt
          </Typography>
        </span>
        {showPendingLegend && (
          <span className="tbm-legendItem">
            <span className="tbm-swatch willUnlock" />
            <Typography variant="text-3" color="subdued" translation={null}>
              Wird aufgehoben
            </Typography>
          </span>
        )}
      </div>
    </div>
  );
};

export default ShiftBlockTimeline;
